import React, { useEffect, useState } from 'react';
import { pageDetails } from '../Recoil/ActiveEvents';
import { useRecoilValue } from 'recoil';
import NewImageGallery from './newImageGallery.jsx';

const ImageGallery = () => {
  const [selectedTab, setSelectedTab] = useState('tab1');
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allImages, setAllImages] = useState([]); // Store all images from API
  const [displayedImages, setDisplayedImages] = useState([]); // Images currently displayed
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const [isLoading, setIsLoading] = useState(false); // Loader state
  const pageSize = 30; // Number of images per page
  const selectedEvent = useRecoilValue(pageDetails);

  // Function to fetch all images from API (no server-side pagination)
  const fetchAllImages = async () => {
    setIsLoading(true);
    try {
      let eventURI = process.env.REACT_APP_API_URL + "/Gallary/GetEventGallary";
      const response = await fetch(eventURI, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          "ImageId": "", 
          "EventId": !!selectedEvent.selectedEID ? selectedEvent.selectedEID : ''
        }),
        redirect: "follow"
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      let evtResult = !!result && !!result.EventGallary ? result.EventGallary : [];
      let allImageData = !!evtResult && evtResult.length > 0 ? evtResult.filter((obj) => obj.FileType === 'image') : [];
      let videoData = !!evtResult && evtResult.length > 0 ? evtResult.filter((obj) => obj.FileType === 'video') : [];
      
      console.log(`Fetched ${allImageData.length} total images from API`);
      
      // Store all images and initialize displayed images with first page
      setAllImages(allImageData);
      setDisplayedImages(allImageData.slice(0, pageSize));
      setVideos(videoData);
      setCurrentPage(1);
      
      // Set initial tab selection
      setSelectedTab('tab2');
      setTimeout(() => {
        setSelectedTab('tab1');
      }, 100);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load - fetch all images from API
  useEffect(() => {
    if (selectedEvent.selectedEID) {
      fetchAllImages();
    }
  }, [selectedEvent.selectedEID]);

  // Function to load more images (client-side pagination)
  const loadMoreImages = () => {
    const nextPage = currentPage + 1;
    const startIndex = 0; // Always start from beginning
    const endIndex = nextPage * pageSize; // Show more images
    
    const newDisplayedImages = allImages.slice(startIndex, endIndex);
    setDisplayedImages(newDisplayedImages);
    setCurrentPage(nextPage);
    
    console.log(`Loaded page ${nextPage}: Showing ${newDisplayedImages.length} of ${allImages.length} images`);
  };
  
  // Check if there are more images to load
  const hasMoreImages = displayedImages.length < allImages.length;

  // Function to open the modal and set the current image
  const openModal = (index) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Function to navigate to the previous image in the modal
  const previousImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + videos.length) % videos.length);
  };

  // Function to navigate to the next image in the modal
  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % videos.length);
  };

  return (
    <div className='pt-10'>
      <div className='max-w-[90vw] mx-auto px-4'>
        <h1 className='text-headerTitle text-h-1 font-bold'>Gallery</h1>
        <div>
          <div className="flex justify-center items-center">
            <button
              onClick={() => setSelectedTab('tab1')}
              className={`rounded-l-[25px] border-2 border-yellow-bg border-r-0 py-3 px-7  ${selectedTab === 'tab1' ? 'bg-yellow-bg font-semibold' : 'bg-sandle-light'}`}
            >
              Images
            </button>
            <button
              onClick={() => setSelectedTab('tab2')}
              className={`rounded-r-[25px] border-2 border-yellow-bg border-l-0 py-3 px-7 ${selectedTab === 'tab2' ? 'bg-yellow-bg font-semibold' : 'bg-sandle-light'}`}
            >
              Videos
            </button>
          </div>

          {/* Tab content */}
          <div className="p-4">
            {selectedTab === 'tab1' && (
              <div>
                {/* Thumbnails */}
                <div>
                  {displayedImages.length === 0 ? (
                    <p className='text-center mt-4 text-lg'>Event images is currently unavailable.</p>
                  ) : (
                    <div className="relative w-full max-w-[90vw] mx-auto mt-10">
                      <NewImageGallery img={displayedImages} />
                      
                      {/* Load More Button */}
                      <div className="flex justify-center items-center mt-8">
                        {/* Debug info - remove in production
                        <div className="text-xs text-gray-400 absolute top-0 left-0">
                          Debug: displayed={displayedImages.length}, total={allImages.length}, hasMore={hasMoreImages.toString()}, page={currentPage}
                        </div>
                         */}
                        
                        {hasMoreImages && displayedImages.length > 0 ? (
                          <button
                            onClick={loadMoreImages}
                            disabled={isLoading}
                            className="bg-yellow-bg hover:bg-yellow-600 disabled:opacity-50 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-200 shadow-lg"
                          >
                            Load More Images ({displayedImages.length} of {allImages.length} loaded)
                          </button>
                        ) : displayedImages.length > 0 ? (
                          <div className="text-center text-gray-500 font-semibold">
                            All images loaded. (Total: {allImages.length})
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedTab === 'tab2' && (
              <div>
                {videos.length === 0 ? (
                  <p className='text-center mt-4 text-lg'>Event video is currently unavailable.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                    {videos.map((obj, index) => (
                      <div
                        key={index}
                        className="cursor-pointer border rounded overflow-hidden shadow-md"
                        onClick={() => openModal(index)}
                      >
                        <img
                          src={"https://kalingarayanrunners.com/assets/images/" + (index + 1) + ".jpg"} //{obj.ImageURL}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-56 object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Modal */}
                {isModalOpen && (
                  <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                    <div className="relative bg-white p-4 rounded shadow-lg">
                      <button
                        className="absolute top-2 right-2 text-gray-700"
                        onClick={closeModal}
                      >
                        &times;
                      </button>
                      <div className="flex items-center justify-between">
                        <button
                          className="bg-gray-200 p-2 px-3 rounded shadow hover:bg-gray-300"
                          onClick={previousImage}
                        >
                          &lt;
                        </button>
                        <video className='max-h-[80vh]' width="640" height="360" controls >
                          <source src={videos[currentIndex].ImageURL} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        <button
                          className="bg-gray-200 p-2 px-3 rounded shadow hover:bg-gray-300"
                          onClick={nextImage}
                        >
                          &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageGallery;